import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Text,
  VStack,
  Image,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  useMediaQuery,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaForward,
  FaBackward,
} from 'react-icons/fa';
import { useAudioPlayer, useAudioTime } from '../context/AudioPlayerContext';
import { formatTime, formatDate } from '../utils/format';

// Live progress for the active episode only — keeps per-second ticks
// from re-rendering every inactive card on the page.
const ActiveProgress = ({ fallbackDuration }) => {
  const { seek } = useAudioPlayer();
  const { currentTime, duration } = useAudioTime();
  const total = duration || fallbackDuration || 100;

  return (
    <Box>
      <Slider
        aria-label="Progress"
        value={currentTime}
        min={0}
        max={total}
        onChange={seek}
        focusThumbOnChange={false}
        size="sm"
      >
        <SliderTrack bg="rgba(255,255,255,0.1)">
          <SliderFilledTrack bg="brand.500" />
        </SliderTrack>
        <SliderThumb boxSize={2.5} />
      </Slider>
      <HStack justify="space-between" mt={1}>
        <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.700">
          {formatTime(currentTime)}
        </Text>
        <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.700">
          {formatTime(total)}
        </Text>
      </HStack>
    </Box>
  );
};

const StaticProgress = ({ duration }) => (
  <Box>
    <Slider aria-label="Progress" value={0} min={0} max={100} isReadOnly focusThumbOnChange={false} size="sm">
      <SliderTrack bg="rgba(255,255,255,0.1)">
        <SliderFilledTrack bg="brand.500" />
      </SliderTrack>
      <SliderThumb boxSize={2.5} />
    </Slider>
    <HStack justify="space-between" mt={1}>
      <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.700">0:00</Text>
      <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.700">{formatTime(duration)}</Text>
    </HStack>
  </Box>
);

const AudioPlayer = ({
  id,
  title,
  audioUrl,
  imageUrl,
  duration,
  pubDate,
}) => {
  const {
    currentEpisode,
    isPlaying: globalIsPlaying,
    error,
    volume,
    isMuted,
    playEpisode,
    skip,
    setVolume,
    toggleMute,
  } = useAudioPlayer();
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isMobile] = useMediaQuery('(max-width: 48em)');

  const isActive = currentEpisode?.id === id;
  const isPlaying = isActive && globalIsPlaying;

  const handlePlay = () => {
    playEpisode({ id, title, audioUrl, imageUrl, durationSec: duration, pubDate });
  };

  return (
    <Box
      background="rgba(255,150,50,0.04)"
      backdropFilter="blur(12px)"
      borderRadius="xl"
      p={4}
      border="1px solid"
      borderColor={isPlaying ? 'rgba(255,152,0,0.45)' : 'rgba(255,150,50,0.10)'}
      boxShadow={isPlaying ? '0 0 30px rgba(255,152,0,0.15)' : 'none'}
      w="full"
      position="relative"
      overflow="hidden"
      _hover={{
        borderColor: 'rgba(255,152,0,0.3)',
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 40px rgba(255,152,0,0.08)',
      }}
      transition="all 0.25s"
    >
      <Box position="absolute" top={0} left={0} right={0} h="2px" bgGradient="linear(to-r, transparent, brand.500, transparent)" />

      <VStack spacing={{ base: 2, md: 4 }} align="stretch">
        {isActive && error && (
          <Alert status="warning" borderRadius="md" bg="rgba(255,150,50,0.12)" border="1px solid rgba(255,150,50,0.25)" color="whiteAlpha.900">
            <AlertIcon color="brand.400" />
            <AlertDescription fontSize="sm">Audio unavailable. Try refreshing or check your connection.</AlertDescription>
          </Alert>
        )}
        <Flex gap={{ base: 3, md: 4 }}>
          {imageUrl && (
            <Box
              flexShrink={0}
              width={{ base: "70px", sm: "80px", md: "120px" }}
              height={{ base: "70px", sm: "80px", md: "120px" }}
            >
              <Image
                src={imageUrl}
                alt={title}
                width="100%"
                height="100%"
                objectFit="cover"
                borderRadius="lg"
                fallback={<Box w="100%" h="100%" borderRadius="lg" bg="rgba(255,150,50,0.08)" />}
              />
            </Box>
          )}
          <VStack flex="1" align="start" spacing={{ base: 1, md: 2 }}>
            <Text
              fontSize={{ base: "sm", sm: "md", md: "lg" }}
              fontWeight="bold"
              color="whiteAlpha.900"
              noOfLines={2}
            >
              {title}
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Text fontSize={{ base: "sm", md: "sm" }} color="whiteAlpha.500" letterSpacing="wide" textTransform="uppercase">
                {formatDate(pubDate)}
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.300">·</Text>
              <Text fontSize={{ base: "sm", md: "sm" }} color="brand.400" letterSpacing="wide" textTransform="uppercase">
                {formatTime(duration)}
              </Text>
            </HStack>

            <HStack spacing={{ base: 2, md: 4 }} width="100%">
              <IconButton
                icon={<FaBackward />}
                aria-label="Skip backward"
                variant="ghost"
                colorScheme="whiteAlpha"
                onClick={() => isActive && skip(-10)}
                isDisabled={!isActive}
                size={{ base: "xs", sm: "sm", md: "md" }}
              />
              <IconButton
                icon={isPlaying ? <FaPause /> : <FaPlay />}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                onClick={handlePlay}
                colorScheme="brand"
                size={{ base: "sm", sm: "md", md: "lg" }}
                isRound
                className={isPlaying ? 'playing-pulse' : ''}
                boxShadow={isPlaying ? '0 0 20px rgba(255,152,0,0.5)' : 'none'}
              />
              <IconButton
                icon={<FaForward />}
                aria-label="Skip forward"
                variant="ghost"
                colorScheme="whiteAlpha"
                onClick={() => isActive && skip(10)}
                isDisabled={!isActive}
                size={{ base: "xs", sm: "sm", md: "md" }}
              />
              <Box position="relative" onMouseLeave={() => setShowVolumeSlider(false)}>
                <IconButton
                  icon={isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={toggleMute}
                  onMouseEnter={() => setShowVolumeSlider(true)}
                  size={{ base: "xs", sm: "sm", md: "md" }}
                />
                {showVolumeSlider && !isMobile && (
                  <Box
                    position="absolute"
                    bottom="100%"
                    left="50%"
                    transform="translateX(-50%)"
                    p={2}
                    background="rgba(21,13,4,0.95)"
                    backdropFilter="blur(12px)"
                    border="1px solid rgba(255,150,50,0.10)"
                    borderRadius="md"
                    width="100px"
                    zIndex={2}
                  >
                    <Slider
                      aria-label="Volume"
                      defaultValue={volume}
                      min={0}
                      max={1}
                      step={0.1}
                      onChange={setVolume}
                    >
                      <SliderTrack bg="rgba(255,255,255,0.1)">
                        <SliderFilledTrack bg="brand.500" />
                      </SliderTrack>
                      <SliderThumb boxSize={3} />
                    </Slider>
                  </Box>
                )}
              </Box>
            </HStack>
          </VStack>
        </Flex>

        {isActive ? (
          <ActiveProgress fallbackDuration={duration} />
        ) : (
          <StaticProgress duration={duration} />
        )}
      </VStack>
    </Box>
  );
};

export default AudioPlayer;
